/**
 * InfoTip — hover/click tooltip rendered via React portal into document.body.
 *
 * Uses getBoundingClientRect() + createPortal so the tooltip is NEVER clipped
 * by parent overflow:hidden, z-index stacking contexts, or transform layers.
 * Works on every admin screen and inside modals/drawers/tables.
 *
 * Usage:
 *   import InfoTip from '../../components/InfoTip'
 *   <InfoTip text="Explains this thing." position="top" />
 *
 * position: 'top' | 'bottom' | 'left' | 'right'  (default: 'top')
 */

import { useState, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'

const TRANSFORMS = {
  top:    'translate(-50%, -100%) translateY(-8px)',
  bottom: 'translate(-50%,    0)  translateY(8px)',
  right:  'translate(0,   -50%)  translateX(8px)',
  left:   'translate(-100%, -50%) translateX(-8px)',
}

export default function InfoTip({ text, position = 'top', maxWidth = 240 }) {
  const [pos,    setPos]    = useState(null)   // {top, left, dir} when visible
  const btnRef              = useRef(null)
  const hideTimer           = useRef(null)

  const darken  = useCallback(() => { if (btnRef.current) btnRef.current.style.color = '#6B7280' }, [])
  const restore = useCallback(() => { if (btnRef.current) btnRef.current.style.color = '#9CA3AF' }, [])

  const show = useCallback(() => {
    clearTimeout(hideTimer.current)
    if (!btnRef.current) return
    const r   = btnRef.current.getBoundingClientRect()
    const sx  = window.scrollX ?? document.documentElement.scrollLeft ?? 0
    const sy  = window.scrollY ?? document.documentElement.scrollTop  ?? 0
    let top, left

    switch (position) {
      case 'top':    top = r.top + sy;              left = r.left + sx + r.width / 2; break
      case 'bottom': top = r.bottom + sy;           left = r.left + sx + r.width / 2; break
      case 'right':  top = r.top + sy + r.height / 2; left = r.right  + sx;           break
      case 'left':   top = r.top + sy + r.height / 2; left = r.left   + sx;           break
      default:       top = r.top + sy;              left = r.left + sx + r.width / 2
    }
    setPos({ top, left, dir: position })
  }, [position])

  const hide = useCallback(() => {
    hideTimer.current = setTimeout(() => setPos(null), 150)
  }, [])

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="More info"
        onMouseEnter={() => { darken();  show()  }}
        onMouseLeave={() => { restore(); hide()  }}
        onFocus={show}
        onBlur={hide}
        onClick={e => { e.stopPropagation(); pos ? setPos(null) : show() }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, lineHeight: 1,
          background: 'none', border: 'none', padding: '0 2px',
          cursor: 'pointer', color: '#9CA3AF',
          verticalAlign: 'middle', userSelect: 'none',
          transition: 'color 0.1s',
        }}
      >
        ⓘ
      </button>

      {pos && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top:    pos.top,
            left:   pos.left,
            transform: TRANSFORMS[pos.dir] ?? TRANSFORMS.top,
            zIndex: 2147483647,          // max z-index — always on top
            background: '#111827',
            color: '#F3F4F6',
            fontSize: 11,
            lineHeight: 1.55,
            padding: '8px 12px',
            borderRadius: 8,
            boxShadow: '0 6px 28px rgba(0,0,0,0.55)',
            maxWidth,
            whiteSpace: 'normal',
            pointerEvents: 'none',
            wordBreak: 'break-word',
          }}
        >
          {text}
        </div>,
        document.body,
      )}
    </>
  )
}
